import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useKeycloak } from "@react-keycloak/web";
import Swal from "sweetalert2";
import ProjectTemplate from "../../Templates/Project";

import ProjectService from '../../../services/Projects/ProjectService';
import KnowledgeBaseService from '../../../services/KnowledgeBase/KnowledgeBaseService';
import WorkspaceService from '../../../services/Workspace/WorkspaceService';

export default function ProjectPage() {
  const params = useParams();
  
  const { keycloak, initialized } = useKeycloak();
  const KEYCLOACK_TOKEN = keycloak.token;

  const [project, setProject] = useState(null);
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState([]);
  const [workspaceItems, setWorkspaceItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (params.id) {
      loadProject();
      loadKnowledgeBaseFiles();
      loadWorkspaceItems();
    }
    // eslint-disable-next-line 
  }, [initialized, keycloak.token, params.id]);
  
  const loadProject = async () => {
    try {
      if (initialized && keycloak.token) {
        setLoading(true);
        const currentProject = await ProjectService.getProjectById(params.id, KEYCLOACK_TOKEN);
        setProject(currentProject);
      }
    } catch (error) {
      Swal.fire({
        position: "top-end",
        title: "Erro ao buscar projeto",
        showConfirmButton: false,
        timer: 1800,
        background: '#27272A',
        color: '#fff',
      });
      setProject(null);
    } finally {
      setLoading(false);
    }
  }

  const loadKnowledgeBaseFiles = async () => {
    try {
      const knowledgeBaseFiles = await KnowledgeBaseService.getAllFilesFromProject(params.id, KEYCLOACK_TOKEN);
      setKnowledgeBaseFiles(knowledgeBaseFiles !== undefined ? knowledgeBaseFiles : []);
    } catch (error) {
      Swal.fire({
        position: "top-end",
        title: "Erro ao buscar base de conhecimento do projeto",
        showConfirmButton: false,
        timer: 1800,
        background: '#27272A',
        color: '#fff',
      });
      setKnowledgeBaseFiles([]);
    }
  }

  const loadWorkspaceItems = async () => {
    try {
      const workspaceItems = await WorkspaceService.getWorkspaceByProjectId(params.id, KEYCLOACK_TOKEN);
      setWorkspaceItems(workspaceItems !== undefined ? workspaceItems : []);
    } catch (error) {
      Swal.fire({
        position: "top-end",
        title: "Erro ao buscar trabalhos concluídos no projeto",
        showConfirmButton: false,
        timer: 1800,
        background: '#27272A',
        color: '#fff',
      });
      console.error('Error loading workspace items:', error);
    }
  }

  const handleDeleteProject = async (projectId) => {
    try {
      await ProjectService.deleteProjectById(projectId, KEYCLOACK_TOKEN);
  
      Swal.fire({
        position: "top-end",
        title: "Projeto excluído com sucesso",
        showConfirmButton: false,
        timer: 1800,
        background: '#27272A',
        color: '#fff',
      });
  
      loadKnowledgeBaseFiles();
    } catch (error) {
      Swal.fire({
        icon: "error",
        position: "top-end",
        title: "Erro ao excluir projeto",
        showConfirmButton: false,
        timer: 1800,
        background: '#27272A',
        color: '#fff',
      });
    }
  }

  const handleFavoriteProject = async (projectId, isFavorite) => {
    try {
      setLoading(true);
      await ProjectService.favoriteProjectById(projectId, KEYCLOACK_TOKEN);
  
      Swal.fire({
        position: "top-end",
        title: isFavorite ? "Projeto removido dos favoritos com sucesso" : "Projeto favoritado com sucesso",
        showConfirmButton: false,
        timer: 1800,
        background: '#27272A',
        color: '#fff',
      });

      loadProject();
    } catch (error) {
      Swal.fire({
        icon: "error",
        position: "top-end",
        title: "Erro ao atualizar projeto",
        showConfirmButton: false,
        timer: 1800,
        background: '#27272A',
        color: '#fff',
      });
    } finally {
      setLoading(false);
    }
  }

  const handleDownloadFile = async (fileId) => {
    try {
      const file = await KnowledgeBaseService.getFileById(fileId, KEYCLOACK_TOKEN);
      
      const response = await fetch(file.downloadUrl);
      
      if (!response.ok) {
        throw new Error('Failed to fetch file');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name || 'download';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);

      Swal.fire({
        position: "top-end",
        title: "Download do arquivo realizado com sucesso",
        showConfirmButton: false,
        timer: 1800,
        background: '#27272A',
        color: '#fff',
      });
    } catch (error) {
      console.error('Error downloading file:', error);
      Swal.fire({
        position: "top-end",
        title: "Erro ao baixar arquivo",
        showConfirmButton: false,
        timer: 1800,
        background: '#27272A',
        color: '#fff',
      });
    }
  }

  const handleRenameFile = async (fileId, newName) => {
    try {
      await KnowledgeBaseService.renameFile(fileId, newName, KEYCLOACK_TOKEN);

      Swal.fire({
        position: "top-end",
        title: "Arquivo renomeado com sucesso",
        showConfirmButton: false,
        timer: 1800,
        background: '#27272A',
        color: '#fff',
      });

      await loadKnowledgeBaseFiles();
    } catch (error) {
      console.error('Error renaming file:', error);
      Swal.fire({
        position: "top-end",
        title: "Erro ao renomear arquivo",
        showConfirmButton: false,
        timer: 1800,
        background: '#27272A',
        color: '#fff',
      });
    }
  }

  return (
    <ProjectTemplate
        project={project}
        loadingProject={loading}
        deleteFunction={handleDeleteProject}
        favoriteFunction={handleFavoriteProject}
        downloadFileFunction={handleDownloadFile}
        renameFileFunction={handleRenameFile}
        knowledgeBaseFiles={knowledgeBaseFiles}
        loadKnowledgeBaseFiles={loadKnowledgeBaseFiles}
        workspaceItems={workspaceItems}
        loadWorkspaceItems={loadWorkspaceItems}
    />
  )
}